import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { keyBy, groupBy, flatMap } from 'lodash'
import { useAuthentication } from './AuthenticationContext'
import { useHetznerProjectsSafe } from './HetznerProjectsContext'
import usePageVisibility from '../shared/hooks/usePageVisibility'
import fetchJSON from '../shared/resources/fetchJSON'
import { API_HOST_NAME } from '../shared/resources/api/v1/constants'

const REFRESH_INTERVAL_MS = 5 * 1000

const HetznerServersContext = createContext(null)

function HetznerServersProvider({ children }) {
  const { teamSlug } = useParams()
  const isVisible = usePageVisibility()
  const { getValidToken } = useAuthentication()
  const { projects, loading: projectsLoading } = useHetznerProjectsSafe()
  const [serversByProject, setServersByProject] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const loadingRef = useRef(false)
  const intervalRef = useRef(null)
  const initialLoadRef = useRef(true)
  const projectsRef = useRef(projects)
  projectsRef.current = projects

  const load = useCallback(() => {
    const currentProjects = projectsRef.current
    if (loadingRef.current || !teamSlug || projectsLoading || currentProjects.length === 0) {
      return Promise.resolve([])
    }
    loadingRef.current = true
    setError(null)

    return getValidToken()
      .then((accessToken) => {
        const fetchPromises = currentProjects.map((project) => {
          const url = `${API_HOST_NAME}/api/v1/cloud/teams/${teamSlug}/hetzner/projects/${project.id}/servers?limit=500`
          const options = {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }

          return fetchJSON(url, 'GET', options)
            .then((response) => ({
              projectId: project.id,
              servers: response.servers || [],
            }))
            .catch(() => ({
              projectId: project.id,
              servers: [],
            }))
        })

        return Promise.all(fetchPromises)
      })
      .then((results) => {
        const newServersByProject = {}
        results.forEach(({ projectId, servers }) => {
          newServersByProject[projectId] = servers
        })
        setServersByProject(newServersByProject)
        return flatMap(results, 'servers')
      })
      .catch((err) => {
        setError(err)
        throw err
      })
      .finally(() => {
        setLoading(false)
        loadingRef.current = false
      })
  }, [teamSlug, projectsLoading, getValidToken])

  const loadRef = useRef(load)
  loadRef.current = load

  // Initial load when projects become available
  useEffect(() => {
    if (!teamSlug || projectsLoading) {
      return
    }

    if (projects.length === 0) {
      setServersByProject({})
      setLoading(false)
      return
    }

    if (initialLoadRef.current) {
      setLoading(true)
      initialLoadRef.current = false
    }

    loadRef.current()
  }, [teamSlug, projectsLoading, projects.length])

  // Polling: start/stop based on visibility
  useEffect(() => {
    if (!teamSlug || projectsLoading || projectsRef.current.length === 0) {
      return
    }

    if (isVisible) {
      loadRef.current()
      intervalRef.current = setInterval(() => loadRef.current(), REFRESH_INTERVAL_MS)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [teamSlug, isVisible, projectsLoading])

  const servers = useMemo(() => {
    return flatMap(Object.values(serversByProject))
  }, [serversByProject])

  const serversById = useMemo(() => keyBy(servers, 'id'), [servers])

  const serversByProjectId = useMemo(() => {
    return groupBy(servers, (server) => server.project?.id)
  }, [servers])

  const deleteServer = useCallback((server) => {
    const url = `${API_HOST_NAME}/api/v1/cloud/teams/${teamSlug}/hetzner/projects/${server.project.id}/servers/${server.id}`
    return getValidToken()
      .then((accessToken) => fetchJSON(url, 'DELETE', {
        headers: { Authorization: `Bearer ${accessToken}` },
      }))
      .then(() => load())
  }, [teamSlug, getValidToken, load])

  const value = useMemo(() => ({
    servers,
    serversById,
    serversByProjectId,
    loading: loading || projectsLoading,
    error,
    reload: load,
    deleteServer,
  }), [servers, serversById, serversByProjectId, loading, projectsLoading, error, load, deleteServer])

  return (
    <HetznerServersContext.Provider value={value}>
      {children}
    </HetznerServersContext.Provider>
  )
}

function useHetznerServers() {
  const context = useContext(HetznerServersContext)
  if (!context) {
    throw new Error('useHetznerServers must be used within a HetznerServersProvider')
  }
  return context
}

function useHetznerServersSafe() {
  const context = useContext(HetznerServersContext)
  return context || { servers: [], serversById: {}, serversByProjectId: {}, loading: false, error: null, reload: () => Promise.resolve([]), deleteServer: () => Promise.resolve() }
}

export { HetznerServersProvider, useHetznerServers, useHetznerServersSafe }
